import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import {EmailTemplate} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {Link} from 'shells/link/link';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {useGetSetting} from '../../../core/use-get-setting';
import {fetchSchemaAction} from '../../../forms/forms-actions';
import {getFormSchema} from '../../../forms/forms-selectors';

import type {NutshellState} from '../../../../store';

import {AddMergeFieldHeader} from './add-merge-field-header';
import {CustomFields} from './custom-fields';
import {StandardTags} from './standard-tags';

import styles from './add-merge-field.module.css';

export type BeeResolveFunction = (arg1: {name: string; value: string}) => void;
export type BeeRejectFunction = () => void;

export type View = 'standard' | 'custom';

export type MergeTagOption = {
    label: string;
    value: string;
    fallback:
        | {
              defaultValue: string;
              placeholder: string;
          }
        | null
        | undefined;
    settingsLinkText?: React.ReactNode;
    details?: React.ReactNode;
    icon?: React.ComponentType<any>;
};

type Props = {
    onClose: () => void;
    resolve: BeeResolveFunction;
    reject: BeeRejectFunction;
};

export function AddMergeField(props: Props) {
    const [selectedMergeTag, setSelectedMergeTag] = React.useState<
        MergeTagOption | null | undefined
    >(null);
    const [currentView, setCurrentView] = React.useState<View>('standard');
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [fallbackValue, setFallbackValue] = React.useState<string>('');

    const dispatch = useDispatch();
    const formSchema = useSelector((state: NutshellState) => getFormSchema(state));

    React.useEffect(() => {
        // @ts-expect-error
        dispatch(fetchSchemaAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {user} = useGetSessionUser();
    const {setting, isLoading} = useGetSetting({
        settingName: EmailTemplate.STREET_ADDRESS_SETTING_NAME,
    });

    const address = setting && setting.value;

    const settingsLink =
        user && user.permissions.canAccessSetup ? (
            <Link
                // @ts-expect-error
                as={RoutedLink}
                size='small'
                variant='primary'
                to='/setup/index?addressFocus=1'
                newTab={true}
            >
                settings
            </Link>
        ) : (
            'settings'
        );

    const getMainContent = (view: View) => {
        if (view === 'standard') {
            return (
                <StandardTags
                    selectedMergeTag={selectedMergeTag}
                    setSelectedMergeTag={(option: MergeTagOption) => {
                        setSelectedMergeTag(option);
                        setFallbackValue(option.fallback ? option.fallback.defaultValue : '');
                    }}
                    settingsLink={settingsLink}
                    address={address}
                    isLoading={isLoading}
                    fallbackValue={fallbackValue}
                    setFallbackValue={setFallbackValue}
                    setCurrentView={setCurrentView}
                />
            );
        }

        if (view === 'custom') {
            return (
                <CustomFields
                    formSchema={formSchema}
                    selectedMergeTag={selectedMergeTag}
                    setSelectedMergeTag={setSelectedMergeTag}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    fallbackValue={fallbackValue}
                    setFallbackValue={setFallbackValue}
                />
            );
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={props.onClose}
            maxWidth={500}
            maxHeight={550}
            closeButton={false}
        >
            <AddMergeFieldHeader
                currentView={currentView}
                setCurrentView={setCurrentView}
                setSearchValue={setSearchValue}
                setSelectedMergeTag={setSelectedMergeTag}
            />
            {getMainContent(currentView)}
            <div className={styles.footer}>
                <FooterBar
                    left={
                        <Button
                            variant='text-secondary'
                            size='big'
                            onClick={() => props.reject()}
                            noPadding={true}
                        >
                            Cancel
                        </Button>
                    }
                    right={
                        <Button
                            variant='save'
                            size='big'
                            onClick={() => {
                                if (!selectedMergeTag) {
                                    // This won't happen, and is only for Flow
                                    return;
                                }

                                props.resolve({
                                    // Name is not shown in the UI, but is still
                                    // required for BEE
                                    name: selectedMergeTag.label,
                                    // The value is in the format of {[MERGE_TAG]}
                                    // or {[MERGE_TAG]|"[FALLBACK_VALUE]"}
                                    value:
                                        fallbackValue.length > 0
                                            ? `{${selectedMergeTag.value}|"${fallbackValue}"}`
                                            : `{${selectedMergeTag.value}}`,
                                });
                            }}
                            disabled={!selectedMergeTag}
                        >
                            Add
                        </Button>
                    }
                />
            </div>
        </Modal>
    );
}
